import { useCallback, type ReactElement } from 'react'

import { IconSprites } from '@constants/enums/sprites'

import { Button } from '@components/ui-core/button/button'
import { Column } from '@components/ui-core/column/column'
import { Icon } from '@components/ui-core/icon/icon'
import { Row } from '@components/ui-core/row/row'

import { Plan } from '../plan/plan'
import { PlanKinds, type PlansPageData, type PlanKind, type PlansPageFlag } from '../types'
import { isCtaDisabled } from '../utils'

import styles from './plans-comparison.module.scss'

export function PlansComparison({
  data,
  highlighted,
  plansPageFlags,
  featuresRef,
}: {
  readonly highlighted: PlanKind
  readonly data: PlansPageData['plansComparisonSection']
  readonly plansPageFlags: Set<PlansPageFlag>
  readonly featuresRef: React.RefObject<HTMLElement | null>
}): ReactElement {
  const isPlanCtaDisabled = isCtaDisabled(plansPageFlags)
  const scrollToFeatures = useCallback(() => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [featuresRef])

  return (
    <Column
      mainAxisAlignment="start"
      crossAxisAlignment="center"
      className={styles.plansComparisonSection}
      gap="10x"
    >
      <Row
        className={styles.container}
        mainAxisAlignment="spaceAround"
        crossAxisAlignment="stretch"
      >
        {PlanKinds.map(kind => (
          <Plan
            key={kind}
            plan={data.plans[kind]}
            highlighted={highlighted}
            ctaDisabled={isPlanCtaDisabled(kind)}
          />
        ))}
      </Row>
      <Button onClick={scrollToFeatures} category="secondary" className={styles.scrollToFeatures}>
        <Row gap="3x" mainAxisAlignment="spaceBetween" crossAxisAlignment="center">
          <span>{data.scrollToFeatures}</span>
          <Icon variant={IconSprites.ThinArrowDown} />
        </Row>
      </Button>
    </Column>
  )
}
